import { DateHelper } from "@/Common/Helpers/DateHelper";
import { CareComponentFilterSetting } from "../Models/CareComponentFilterSetting";
import { CareElementFrequencyType } from "../Models/CareElementFrequencyType";
import { LabResultObservation } from "../Models/CareElements/LabResultObservation";
import { LabResultsCareElement } from "../Models/CareElements/LabResultsCareElement";
import { ResultsDurationType } from "../Models/ResultsDurationType";

interface FlattenedLabObservation {
    observation: LabResultObservation;
    frequency?: CareElementFrequencyType;
    isOutsideTarget: boolean;
    isPastDue: boolean;
    name: string;
}

export class FlowsheetLabResultFilterHelper {
    public static filterCareElements(
        careElements: LabResultsCareElement[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): LabResultsCareElement[] {
        const filteredCareElements: LabResultsCareElement[] = careElements
            .filter(el =>
                careComponentFilterSettings.CareElements.some(x => x.Key === el.Name && x.IsEnabled)
            )
            .map(el => ({
                ...el,
                Observations: el.Observations
            }));

        return filteredCareElements;
    }

    public static filterLabResultObservations(
        careElements: LabResultsCareElement[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): void {
        const allFilteredObservations = FlowsheetLabResultFilterHelper.getObservationsByDateRangeAndByIssue(careElements, careComponentFilterSettings);
        const finalFiltered = FlowsheetLabResultFilterHelper.filterByDurationType(
            FlowsheetLabResultFilterHelper.getSortedObservationsByDate(allFilteredObservations),
            careComponentFilterSettings
        );
        FlowsheetLabResultFilterHelper.assignObservationsToCareElements(careElements, finalFiltered);
    }

    private static filterByDateRange(
        observations: FlattenedLabObservation[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedLabObservation[] {
        if (careComponentFilterSettings.StartDate || careComponentFilterSettings.EndDate) {
            const start = careComponentFilterSettings.StartDate ? new Date(careComponentFilterSettings.StartDate) : undefined;
            const end = careComponentFilterSettings.EndDate ? new Date(careComponentFilterSettings.EndDate) : undefined;

            observations = observations.filter(({ observation }) => {
                const obsDate = new Date(DateHelper.formatDateLuxon(observation.Date, "yyyy-MM-dd"));
                return (!start || obsDate >= start) && (!end || obsDate <= end);
            });
        }

        return observations;
    }

    private static filterByTreatmentIssues(
        observations: FlattenedLabObservation[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedLabObservation[] {
        if (careComponentFilterSettings.OutsideTreatmentTarget || careComponentFilterSettings.OutsideTreatmentFrequency) {
            observations = observations.filter(p =>
                (careComponentFilterSettings.OutsideTreatmentTarget && p.isOutsideTarget) ||
                (careComponentFilterSettings.OutsideTreatmentFrequency && p.isPastDue)
            );
        }

        return observations;
    }

    private static filterByDurationType(
        observations: FlattenedLabObservation[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedLabObservation[] {
        const groupedObservations: { [date: string]: FlattenedLabObservation[] } =
            FlowsheetLabResultFilterHelper.groupObservationsByDate(observations);
        const sortedDates: string[] =
            FlowsheetLabResultFilterHelper.getObservationsByDateDescending(groupedObservations);

        const sliceCount = FlowsheetLabResultFilterHelper.getSliceCount(careComponentFilterSettings.ResultsDurationType);
        if (sliceCount === null)
            return observations;

        return sortedDates.slice(0, sliceCount).flatMap(date => groupedObservations[date]);
    }

    private static groupObservationsByDate(
        observations: FlattenedLabObservation[]
    ): { [date: string]: FlattenedLabObservation[] } {
        const grouped: { [date: string]: FlattenedLabObservation[] } = {};

        for (const obs of observations) {
            const date = obs.observation.Date;
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(obs);
        }

        return grouped;
    }

    private static getObservationsByDateDescending(
        grouped: { [date: string]: FlattenedLabObservation[] }
    ): string[] {
        return Object.keys(grouped).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    }

    private static getSliceCount(durationType: ResultsDurationType): number | null {
        switch (durationType) {
            case ResultsDurationType.Latest:
                return 1;
            case ResultsDurationType.Last2:
                return 2;
            case ResultsDurationType.Last3:
                return 3;
            default:
                return null;
        }
    }

    private static getObservationsByDateRangeAndByIssue(
        careElements: LabResultsCareElement[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedLabObservation[] {
        const results: FlattenedLabObservation[] = [];

        for (const careElement of careElements) {
            const labObservations: FlattenedLabObservation[] = careElement.Observations.map(observation => ({
                observation,
                frequency: careElement.Frequency,
                isOutsideTarget: observation.IsOutsideOfTargetValue,
                isPastDue: careElement.IsPastDue,
                name: careElement.Name
            }));

            const observationsByDateRange =
                FlowsheetLabResultFilterHelper.filterByDateRange(labObservations, careComponentFilterSettings);
            const observationsByTreatmentIssue =
                FlowsheetLabResultFilterHelper.filterByTreatmentIssues(observationsByDateRange, careComponentFilterSettings);

            results.push(...observationsByTreatmentIssue);
        }

        return results;
    }

    private static getSortedObservationsByDate(
        observations: FlattenedLabObservation[]
    ): FlattenedLabObservation[] {
        return observations.sort(
            (a, b) =>
                new Date(b.observation.Date).getTime() -
                new Date(a.observation.Date).getTime()
        );
    }

    private static assignObservationsToCareElements(
        careElements: LabResultsCareElement[],
        observations: FlattenedLabObservation[]
    ): void {
        const grouped = new Map<string, LabResultObservation[]>();

        for (const entry of observations) {
            if (!grouped.has(entry.name)) {
                grouped.set(entry.name, []);
            }
            grouped.get(entry.name)!.push(entry.observation);
        }

        for (const careElement of careElements) {
            careElement.Observations = grouped.get(careElement.Name) ?? [];
        }
    }
}
